import { useEffect, useMemo, useState } from 'react';
import { csvEscape } from '../utils/csv.js';

// Pivot-style count grid over the currently filtered contacts: pick a row
// dimension and a column dimension, each cell is the number of matching
// contacts. Lives behind the "Breakdown" button next to the CSV download on
// the Contacts toolbar. The grid is meant to be pasted/screenshotted into
// updates, so it also offers copy-as-TSV (pastes straight into Sheets/Slack)
// and a CSV download.

const BLANK = '(blank)';

// The derived sector columns are NULL for three distinct reasons — say
// which, instead of one opaque "(blank)" bucket. Generalists are blank BY
// DESIGN (their industry focus isn't meaningful); specialists are only
// blank when BigDough has no industry focus for them (the dictionary maps
// every observed token).
export function sectorBlankLabel(row) {
  const cov = String(row.sector_coverage ?? '').toLowerCase();
  if (cov.includes('generalist')) return 'Generalist';
  if (cov.includes('specialist')) return 'Specialist (no industry focus)';
  return '(no coverage data)';
}

// Pseudo-rows from blank cells sort after real values, in this order.
const BLANK_LABEL_ORDER = ['Generalist', 'Specialist (no industry focus)', '(no coverage data)', BLANK];

// `tags` dimensions are comma-separated multi-value cells — a contact with
// two packs lands in both rows. `get` dimensions derive their value;
// `blank` labels a row/column with no value for this dimension.
const DIMENSIONS = [
  { key: 'sector_packs',    label: 'Sector Pack',      tags: true, blank: sectorBlankLabel },
  { key: 'implied_sectors', label: 'Implied Sector',   tags: true, blank: sectorBlankLabel },
  { key: 'industry_focus',  label: 'Industry Focus',   tags: true },
  { key: 'sector_coverage', label: 'Coverage',         tags: true },
  { key: 'approved',        label: 'Approved (status)',
    get: (r) => [String(r.bd_status ?? '').toLowerCase() === 'approved' ? 'Approved' : 'Not approved'] },
  { key: 'bd_status',       label: 'Status' },
  { key: '_stage',          label: 'Stage' },
  { key: 'job_function',    label: 'Job Function',     tags: true },
  { key: 'institution_type',label: 'Institution Type', tags: true },
  { key: '_fund_type',      label: 'Fund Type' },
  { key: '_firm_owner',     label: 'Owner' },
  { key: 'side',            label: 'Side',             tags: true },
  { key: 'focus_region',    label: 'Focus Region',     tags: true },
];

function splitCell(value) {
  return String(value ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}

function dimValues(dim, row) {
  const vals = dim.get
    ? dim.get(row)
    : dim.tags
      ? [...new Set(splitCell(row[dim.key]))]
      : [String(row[dim.key] ?? '').trim()].filter(Boolean);
  return vals.length ? vals : [dim.blank?.(row) ?? BLANK];
}

export default function BreakdownGrid({ onClose, fetchRows, filterSummary = [] }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [rowDimKey, setRowDimKey] = useState('sector_packs');
  const [colDimKey, setColDimKey] = useState('approved');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchRows()
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
    // fetchRows captures the page's current filters; fetch once per open.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rowDim = DIMENSIONS.find((d) => d.key === rowDimKey) ?? DIMENSIONS[0];
  const colDim = DIMENSIONS.find((d) => d.key === colDimKey) ?? DIMENSIONS[4];

  const grid = useMemo(() => {
    if (!rows) return null;
    const cells = new Map();      // `${rv}\u0000${cv}` → count
    const rowTotals = new Map();  // rv → sum of its cells
    const colTotals = new Map();
    for (const r of rows) {
      const rvs = dimValues(rowDim, r);
      const cvs = dimValues(colDim, r);
      for (const rv of rvs) {
        for (const cv of cvs) {
          const k = `${rv}\u0000${cv}`;
          cells.set(k, (cells.get(k) ?? 0) + 1);
          rowTotals.set(rv, (rowTotals.get(rv) ?? 0) + 1);
          colTotals.set(cv, (colTotals.get(cv) ?? 0) + 1);
        }
      }
    }
    // Highest-volume rows/columns first; blank-value pseudo-rows always last.
    const byTotal = (totals) => (a, b) => {
      const ba = BLANK_LABEL_ORDER.indexOf(a);
      const bb = BLANK_LABEL_ORDER.indexOf(b);
      if (ba !== -1 || bb !== -1) {
        if (ba === -1) return -1;
        if (bb === -1) return 1;
        return ba - bb;
      }
      return (totals.get(b) ?? 0) - (totals.get(a) ?? 0) || a.localeCompare(b);
    };
    const rowKeys = [...rowTotals.keys()].sort(byTotal(rowTotals));
    const colKeys = colDim.key === 'approved'
      ? ['Approved', 'Not approved'].filter((k) => colTotals.has(k))
      : [...colTotals.keys()].sort(byTotal(colTotals));
    const multiTag = !!(rowDim.tags || colDim.tags);
    return { cells, rowTotals, colTotals, rowKeys, colKeys, multiTag };
  }, [rows, rowDim, colDim]);

  function tableLines(sep, escape = (s) => String(s)) {
    const { cells, rowTotals, colTotals, rowKeys, colKeys } = grid;
    const grand = [...rowTotals.values()].reduce((a, b) => a + b, 0);
    const lines = [];
    // Carry the view's context along with the numbers.
    if (filterSummary.length) lines.push([`Filters: ${filterSummary.join(' · ')}`].map(escape).join(sep));
    lines.push([rowDim.label, ...colKeys, 'Total'].map(escape).join(sep));
    for (const rv of rowKeys) {
      lines.push([rv, ...colKeys.map((cv) => cells.get(`${rv}\u0000${cv}`) ?? 0), rowTotals.get(rv) ?? 0].map(escape).join(sep));
    }
    lines.push(['Total', ...colKeys.map((cv) => colTotals.get(cv) ?? 0), grand].map(escape).join(sep));
    return lines;
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(tableLines('\t').join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      alert(`Copy failed: ${e.message}`);
    }
  }

  function handleDownload() {
    const csv = tableLines(',', csvEscape).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `breakdown_${rowDim.key}_by_${colDim.key}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const selectStyle = {
    padding: '5px 8px', borderRadius: 7, border: '1px solid var(--c-border)',
    background: 'var(--c-surface)', color: 'var(--c-text)', fontSize: 13,
  };
  const cellBase = { border: '1px solid var(--c-border)', padding: '5px 12px', fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' };
  const headBase = { ...cellBase, fontWeight: 700, background: 'var(--c-bg-2)', position: 'sticky', top: 0 };

  return (
    <div className="panel-overlay" onClick={onClose} style={{ zIndex: 10001 }}>
      <div
        className="panel"
        style={{ width: 'fit-content', minWidth: 460, maxWidth: '95vw', height: 'auto', maxHeight: '90vh', margin: 'auto', borderRadius: 14, display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel__header">
          <div className="panel__title">
            Breakdown{rows ? ` — ${rows.length.toLocaleString()} filtered contact${rows.length === 1 ? '' : 's'}` : ''}
          </div>
          <button className="panel__close" onClick={onClose}>×</button>
        </div>
        <div className="panel__body" style={{ overflow: 'auto', flex: 1 }}>
          {filterSummary.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filtered by</span>
              {filterSummary.map((s) => (
                <span key={s} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 999, background: 'var(--c-bg-2)', border: '1px solid var(--c-border)', color: 'var(--c-text-2)' }}>{s}</span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            <label style={{ fontSize: 12.5, color: 'var(--c-text-2)' }}>Rows</label>
            <select value={rowDim.key} onChange={(e) => setRowDimKey(e.target.value)} style={selectStyle}>
              {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <button
              className="btn btn--ghost btn--sm"
              title="Swap rows and columns"
              onClick={() => { setRowDimKey(colDim.key); setColDimKey(rowDim.key); }}
            >⇄</button>
            <label style={{ fontSize: 12.5, color: 'var(--c-text-2)' }}>Columns</label>
            <select value={colDim.key} onChange={(e) => setColDimKey(e.target.value)} style={selectStyle}>
              {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>

          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>Failed to load contacts: {error}</div>}
          {!error && !grid && <div style={{ fontSize: 13, color: 'var(--c-text-2)', padding: '18px 0' }}>Loading filtered contacts…</div>}
          {grid && (
            <>
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...headBase, textAlign: 'left' }}>{rowDim.label}</th>
                    {grid.colKeys.map((cv) => <th key={cv} style={headBase}>{cv}</th>)}
                    <th style={headBase}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {grid.rowKeys.map((rv) => (
                    <tr key={rv}>
                      <td style={BLANK_LABEL_ORDER.includes(rv)
                        ? { ...cellBase, textAlign: 'left', fontWeight: 400, fontStyle: 'italic', color: 'var(--c-text-2)' }
                        : { ...cellBase, textAlign: 'left', fontWeight: 600 }}>{rv}</td>
                      {grid.colKeys.map((cv) => {
                        const n = grid.cells.get(`${rv}\u0000${cv}`) ?? 0;
                        return <td key={cv} style={{ ...cellBase, color: n === 0 ? 'var(--c-text-3)' : 'var(--c-text)' }}>{n}</td>;
                      })}
                      <td style={{ ...cellBase, fontWeight: 700 }}>{grid.rowTotals.get(rv) ?? 0}</td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...cellBase, textAlign: 'left', fontWeight: 700, background: 'var(--c-bg-2)' }}>Total</td>
                    {grid.colKeys.map((cv) => (
                      <td key={cv} style={{ ...cellBase, fontWeight: 700, background: 'var(--c-bg-2)' }}>{grid.colTotals.get(cv) ?? 0}</td>
                    ))}
                    <td style={{ ...cellBase, fontWeight: 700, background: 'var(--c-bg-2)' }}>
                      {[...grid.rowTotals.values()].reduce((a, b) => a + b, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
              {grid.multiTag && (
                <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', marginTop: 8 }}>
                  Multi-tag contacts count once per matching cell, so totals can exceed the filtered contact count.
                </div>
              )}
            </>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--c-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn--ghost btn--sm" disabled={!grid} onClick={handleCopy}>{copied ? '✓ Copied' : '📋 Copy'}</button>
          <button className="btn btn--ghost btn--sm" disabled={!grid} onClick={handleDownload}>⬇ CSV</button>
          <button className="btn btn--primary btn--sm" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
